import { useCallback, useEffect, useState } from 'react';
import {
	Batch,
	PagingConfig,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useAuth } from '../../../hooks/useAuth';
import { useBatchId } from '../../../hooks/useBatchId';
import { useLoadingIndicator } from '../../../hooks/useLoadingIndicator';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
	allPreworkRecommendationStatuses,
	allPreworkStatuses,
	DEFAULT_PAGE_SIZE,
} from '../../../shared/Constants';
import { getPreworkApplications } from '../../../service/PreWorkService';
import {
	PreworkApplicationFilters,
	PreworkApplicationsResponse,
} from '../../../shared/PreWorkServiceTypes';
import { getBatch } from '../../../service/VolunteerApplicationService';
import { displayError } from '../../../shared/Utils';
import useUserSession from '../../../hooks/useUserSession';

export const usePreworkMentorApplicationsList = () => {
	const [applicationsList, setApplicationsList] =
		useState<PreworkApplicationsResponse>({
			preworkStatus: [],
			pagination: { totalNoOfRecords: 0 },
		});
	const [selectedBatch, setSelectedBatch] = useState<Batch>();
	const { initiated, srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { batchId } = useBatchId();
	const [paging, setPaging] = useState<PagingConfig>({
		pageNo: 1,
		pageSize: DEFAULT_PAGE_SIZE,
	});
	const [filter, setFilter] = useState<PreworkApplicationFilters>();
	const { setLoading } = useLoadingIndicator();
	const { showToastMessage } = useToastMessage();
	const isApprover = selectedRole === UserRole.APPROVER;

	useEffect(() => {
		let defaultFilters: PreworkApplicationFilters | undefined;

		if (!defaultFilters) {
			defaultFilters = {
				preworkStatuses: allPreworkStatuses,
			};
		}

		if (isApprover) {
			defaultFilters = {
				...defaultFilters,
				preworkRecommendation: allPreworkRecommendationStatuses,
			};
		}

		setFilter(defaultFilters);
	}, [isApprover]);

	const onPageChange = useCallback((pageNumber: number, pageSize: number) => {
		setPaging({ pageNo: pageNumber, pageSize });
	}, []);

	const fetchApplicationsList = useCallback(
		async (
			pageNumber: number,
			pageSize: number,
			applicationFilter: PreworkApplicationFilters
		) => {
			if (initiated && srcmId && selectedRole) {
				setLoading(true);
				const pageIndex = pageNumber < 1 ? 0 : pageNumber - 1;
				try {
					const applications = await getPreworkApplications({
						batchId,
						filters: applicationFilter,
						pageIndex,
						pageSize,
						functionarySrcmId: srcmId,
						selectedRoleCode: selectedRole,
					});

					if (
						applications.pagination.totalNoOfPages &&
						applications.pagination.totalNoOfPages > 0 &&
						pageNumber > applications.pagination.totalNoOfPages
					) {
						onPageChange(applications.pagination.totalNoOfPages ?? 1, pageSize);
					} else {
						setApplicationsList(applications);
					}
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				}
				setLoading(false);
			}
		},
		[
			batchId,
			initiated,
			onPageChange,
			selectedRole,
			setLoading,
			showToastMessage,
			srcmId,
		]
	);

	const fetchBatchDetails = useCallback(async () => {
		if (initiated && srcmId) {
			setLoading(true);
			try {
				const batch = await getBatch({ value: batchId });
				setSelectedBatch(batch);
			} catch (e) {
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			}
			setLoading(false);
		}
	}, [batchId, initiated, setLoading, showToastMessage, srcmId]);

	const onFilterChange = useCallback(
		(filterParam: PreworkApplicationFilters) => {
			setFilter(filterParam);
			onPageChange(1, paging.pageSize ?? DEFAULT_PAGE_SIZE);
		},
		[onPageChange, paging.pageSize]
	);

	useEffect(() => {
		if (filter) {
			void fetchApplicationsList(
				paging.pageNo ?? 1,
				paging.pageSize ?? DEFAULT_PAGE_SIZE,
				filter
			);
		}
	}, [paging, filter, fetchApplicationsList]);

	useEffect(() => {
		void fetchBatchDetails();
	}, [fetchBatchDetails]);

	return {
		applicationsList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		selectedBatch,
	};
};
